import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaGavel, FaShieldAlt, FaEnvelope, FaCopyright, FaCheck, FaExclamationTriangle, FaPaperPlane } from 'react-icons/fa';
import { sendContactEmail } from '../../api/strapi';

const PopupOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
`;

const PopupContainer = styled(motion.div)`
  background: white;
  border-radius: 15px;
  width: 90%;
  max-width: 1000px;
  height: 80vh;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
`;

const PopupHeader = styled.div`
  padding: 20px 30px;
  background: linear-gradient(135deg, #0066cc, #004999);
  color: white;
  position: relative;
  display: flex;
  align-items: center;
`;

const PopupTitle = styled.h2`
  font-size: 1.8rem;
  margin: 0;
  flex: 1;
  color: white;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.8rem;
  cursor: pointer;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: rotate(90deg);
  }
`;

const PopupContent = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

// Sidebar harmonisé (colonne sur PC, ligne sur mobile)
const Sidebar = styled.div`
  width: 280px;
  background: #f5f7fa;
  border-right: 1px solid #e1e5eb;
  overflow-y: auto;

  @media (max-width: 768px) {
    width: 100%;
    min-width: 0;
    max-width: 100vw;
    display: flex;
    flex-direction: row;
    overflow-x: auto;
    border-right: none;
    border-bottom: 1px solid #e1e5eb;
    background: #f5f7fa;
    padding: 0 0 2px 0;
  }
`;

const NavItem = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 15px 20px;
  text-align: left;
  background: ${props => props.$active ? 'white' : 'transparent'};
  border: none;
  border-left: 4px solid ${props => props.$active ? '#0066cc' : 'transparent'};
  color: ${props => props.$active ? '#0066cc' : '#667188'};
  font-weight: ${props => props.$active ? '600' : '500'};
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: ${props => props.$active ? 'white' : '#edf0f5'};
    color: #0066cc;
  }

  .icon-container {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  @media (max-width: 768px) {
    width: auto;
    min-width: 120px;
    padding: 12px 10px;
    border-left: none;
    border-bottom: 3px solid ${props => props.$active ? '#0066cc' : 'transparent'};
    border-radius: 0;
    justify-content: center;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  padding: 30px;
  overflow-y: auto;
  color: #333;

  @media (max-width: 768px) {
    width: 100%;
    max-width: 100vw;
    padding: 20px 10px;
  }
`;

const TabContent = styled(motion.div)`
  max-width: 700px;
  margin: 0 auto;
  color: #333;
  
  h2 {
    color: #333;
    font-size: 1.8rem;
    margin-bottom: 1.5rem;
  }
  
  h3 {
    color: #333;
    font-size: 1.4rem;
    margin: 1.5rem 0 1rem;
  }
  
  p {
    color: #333;
    line-height: 1.6;
    margin-bottom: 1rem;
  }
  
  ul {
    color: #333;
    margin-bottom: 1rem;
    padding-left: 1.5rem;
    
    li {
      margin-bottom: 0.5rem;
      color: #333;
    }
  }
  
  strong {
    color: #0066cc;
  }

  div {
    color: #333;
  }
`;

const ContactForm = styled.form`
  margin-top: 2rem;
  background: #f5f7fa;
  padding: 2rem;
  border-radius: 8px;
  
  h3 {
    margin-bottom: 1.5rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  
  label {
    display: block;
    margin-bottom: 0.5rem;
    color: #333;
    font-weight: 500;
  }
  
  input, textarea {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid #e1e5eb;
    border-radius: 4px;
    font-size: 1rem;
    color: #333;
    
    &:focus {
      outline: none;
      border-color: #0066cc;
      box-shadow: 0 0 0 2px rgba(0, 102, 204, 0.1);
    }
  }
  
  textarea {
    min-height: 150px;
    resize: vertical;
  }
`;

const SubmitButton = styled.button`
  background: #0066cc;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover:not(:disabled) {
    background: #004999;
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const LegalPopup = ({ onClose, initialTab = 'mentions-legales' }) => {
  const [activeSection, setActiveSection] = useState(initialTab);
  
  // État pour le formulaire de contact
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const [submitMessage, setSubmitMessage] = useState('');
  
  // Animation variants
  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.3 } }
  };
  
  const popupVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4, delay: 0.1 } }
  };
  
  const contentVariants = {
    hidden: { opacity: 0, x: 20 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.4 } }
  };

  return (
    <AnimatePresence>
      <PopupOverlay
        variants={overlayVariants}
        initial="hidden"
        animate="visible"
        exit="hidden"
        onClick={onClose}
      >
        <PopupContainer 
          variants={popupVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          onClick={e => e.stopPropagation()}
        >
          <PopupHeader>
            <PopupTitle>Informations légales</PopupTitle>
            <CloseButton onClick={onClose}>
              <FaTimes />
            </CloseButton>
          </PopupHeader>
          
          <PopupContent>
            {/* Sidebar desktop */}
            <Sidebar>
              <NavItem 
                $active={activeSection === 'mentions-legales'} 
                onClick={() => setActiveSection('mentions-legales')}
              >
                <div className="icon-container">
                  <FaGavel />
                  <span>Mentions légales</span>
                </div>
              </NavItem>
              <NavItem 
                $active={activeSection === 'confidentialite'} 
                onClick={() => setActiveSection('confidentialite')}
              >
                <div className="icon-container">
                  <FaShieldAlt />
                  <span>Politique de confidentialité</span>
                </div>
              </NavItem>
              <NavItem 
                $active={activeSection === 'contact'} 
                onClick={() => setActiveSection('contact')}
              >
                <div className="icon-container">
                  <FaEnvelope />
                  <span>Contact</span>
                </div>
              </NavItem>
              <NavItem 
                $active={activeSection === 'licence'} 
                onClick={() => setActiveSection('licence')}
              >
                <div className="icon-container">
                  <FaCopyright />
                  <span>Licence</span>
                </div>
              </NavItem>
            </Sidebar>
            <ContentArea>
              <AnimatePresence mode="wait">
                {activeSection === 'mentions-legales' && (
                  <TabContent
                    key="mentions-legales"
                    variants={contentVariants}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                  >
                    <h2>Mentions légales</h2>
                    <p>Les informations suivantes sont fournies en vertu de l'article 6 de la loi n° 2004-575 du 21 juin 2004 pour la Confiance dans l'Economie Numérique.</p>
                    
                    <h3>Éditeur du site</h3>
                    <p><strong>Raison sociale :</strong> OFFICE DES SPORTS DE L'AGGLOMERATION DE LA REGION DE COMPIEGNE (OSARC)</p>
                    <p><strong>Siège social :</strong> Stade Paul-Petitpoisson rue Albert Robida 60200 COMPIEGNE</p>
                    <p><strong>N° de déclaration à la préfecture :</strong> W603000986</p>
                    <p><strong>N° de SIRET :</strong> 30920405500029</p>
                    <p><strong>Téléphone :</strong> 06 52 94 92 82</p>
                    <p><strong>Email :</strong> contact@offisport.com</p>
                    <p><strong>Directeur de la publication :</strong> Philippe GRAND</p>
                    
                    <h3>Hébergement</h3>
                    <p>Le site internet www.offisport.com est hébergé par :</p>
                    <p><strong>OVH</strong></p>
                    <p>SAS au capital de 10.174.560 euros</p>
                    <p>RCS Lille métropole 424 761 419 00045</p>
                    <p>Code APE 2620 Z</p>
                    <p>N° TVA : FR 22 424 761 419</p>
                    <p>Siège social : 2 rue Kellermann 59053 Roubaix – France</p>
                    
                    <p><strong>Crédit photos :</strong> OSARC</p>
                  </TabContent>
                )}
                
                {activeSection === 'confidentialite' && (
                  <TabContent
                    key="confidentialite"
                    variants={contentVariants}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                  >
                    <h2>Politique de confidentialité</h2>
                    
                    <p>Nous nous réservons le droit de modifier à tout moment la présente page. Nous vous invitons par conséquent à la consulter régulièrement afin de vous informer de ses évolutions.</p>
                    
                    <p><strong>Dernière date de mise à jour :</strong> 29 mars 2021</p>
                    
                    <h3>Qui est le responsable du traitement de vos données personnelles ici ?</h3>
                    <p>L'Office des Sports de l'Agglomération de la Région de Compiègne est l'éditeur du site.</p>
                    <p>En consultant nos mentions légales, vous retrouverez l'ensemble de nos coordonnées.</p>
                    
                    <h3>Les traitements relatifs à vos données personnelles :</h3>
                    <p>Offisport.com est un site internet accessible depuis l'adresse https://www.offisport.com</p>
                    
                    <p>Notre site ne demande pas d'enregistrement nominatif à ses visiteurs et ne procède à aucun enregistrement nominatif pour la simple consultation de ses pages. Cependant, dans certains cas (formulaire de contact) vous pouvez être invité à laisser des données personnelles (nom, adresse électronique). Le caractère obligatoire ou facultatif des données vous est signalé lors de la collecte par la mention « obligatoire ». Nous ne collectons aucune donnée sensible, à savoir aucune donnée relative à vos origines raciales ou ethniques, à vos opinions politiques, philosophiques ou religieuses ou votre appartenance syndicale, ou qui sont relatives à votre santé ou votre vie sexuelle.</p>
                    
                    <p>Par ailleurs, lors de la consultation de notre site web, nous sommes amenés à collecter et traiter des données relatives à votre navigation (notamment les cookies, et les pages que vous avez consultées), et à votre terminal (type de navigateur utilisé, modèle et version de votre système d'exploitation, résolution de votre écran, présence de certains plug-ins,…) Ces données seront utilisées pour réaliser des statistiques d'utilisation de notre site afin de le rendre plus pertinent.</p>
                    
                    <h3>Les cookies :</h3>
                    <p>Le site utilise des cookies. Un cookie est un petit fichier stocké pour une durée déterminée dans votre navigateur par un site web, qui permet de renvoyer une information lors de la connexion audit site. Si vous vous rendez sur la page de connexion, un cookie temporaire sera créé afin de déterminer si votre navigateur accepte les cookies. Il ne contient pas de données personnelles et sera supprimé automatiquement à la fermeture de votre navigateur.</p>
                    
                    <p>Lorsque vous vous connectez, un certain nombre de cookies sont mis en place pour enregistrer vos informations de connexion et vos préférences d'écran. La durée de vie d'un cookie de connexion est de deux jours, celle d'un cookie d'option d'écran est d'un an. Si vous cochez « Se souvenir de moi », votre cookie de connexion sera conservé pendant deux semaines. Si vous vous déconnectez de votre compte, le cookie de connexion sera effacé.</p>
                    
                    <h3>Quand ?</h3>
                    <p>Nous collectons vos données notamment quand :</p>
                    <ul>
                      <li>Vous utilisez notre formulaire de contact</li>
                    </ul>
                    
                    <h3>Quelles finalités ?</h3>
                    <p>Nous utilisons vos données personnelles pour :</p>
                    <ul>
                      <li>Vous contacter en réponse à une demande de votre part</li>
                      <li>Avec votre consentement, vous inscrire à une newsletter et/ou vous envoyer des informations relatives à votre demande</li>
                      <li>Nous permettre d'établir des statistiques et comptages de fréquentation de notre site.</li>
                    </ul>
                    
                    <h3>Durée de conservation :</h3>
                    <p>Si vous avez consenti à l'envoi de newsletters et mails d'information, vos données seront conservées durant toute la durée que vous souhaitez. Sur simple demande de votre part, nous supprimerons vos coordonnées.</p>
                    
                    <h3>Vos droits :</h3>
                    <p>Conformément à la loi Informatique et Libertés du 6 janvier 1978 modifiée, vous disposez de droits d'accès, de rectification, d'effacement d'opposition au ou de limitation du traitement de vos données, de retrait de votre consentement.</p>
                    <p>Vous pouvez également donner des directives à notre association concernant l'utilisation de vos données après votre décès.</p>
                    
                    <h3>Qui contacter à propos de vos données personnelles ?</h3>
                    <p>Vous souhaitez exercer vos droits ou manifester une réclamation relative à vos données personnelles traitées par le site : vous pouvez nous contacter directement en consultant nos Mentions Légales ou figurent l'ensemble de nos coordonnées. Dans tous les cas, vous disposez du droit d'introduire une réclamation auprès de la CNIL</p>
                  </TabContent>
                )}
                
                {activeSection === 'contact' && (
                  <TabContent
                    key="contact"
                    variants={contentVariants}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                  >
                    <h2>Contact</h2>
                    <p>Pour toute question concernant les mentions légales, la politique de confidentialité ou la licence d'utilisation, vous pouvez nous contacter :</p>
                    
                    <div style={{ background: '#f5f7fa', padding: '20px', borderRadius: '8px', marginBottom: '30px' }}>
                      <p><strong>Par téléphone :</strong> 06 52 94 92 82</p>
                      <p><strong>Par email :</strong> contact@offisport.com</p>
                      <p><strong>Par courrier :</strong><br />
                      OSARC<br />
                      Stade Paul-Petitpoisson<br />
                      rue Albert Robida<br />
                      60200 COMPIEGNE</p>
                    </div>

                    <ContactForm onSubmit={async (e) => {
                      e.preventDefault();
                      setIsSubmitting(true);
                      setSubmitStatus(null);
                      setSubmitMessage('');

                      try {
                        await sendContactEmail(
                          contactForm.name,
                          contactForm.email,
                          contactForm.subject,
                          contactForm.message
                        );
                        
                        setSubmitStatus('success');
                        setSubmitMessage('Votre message a été envoyé avec succès. Nous vous répondrons dans les plus brefs délais.');
                        
                        // Reset du formulaire après succès
                        setContactForm({
                          name: '',
                          email: '',
                          subject: '',
                          message: ''
                        });
                      } catch (error) {
                        setSubmitStatus('error');
                        setSubmitMessage(
                          error?.response?.data?.error?.message || 
                          'Une erreur est survenue lors de l\'envoi. Veuillez réessayer plus tard.'
                        );
                        console.error('Erreur envoi email:', error);
                      } finally {
                        setIsSubmitting(false);
                      }
                    }}>
                      <h3>Envoyez-nous un message</h3>
                      
                      <FormGroup>
                        <label htmlFor="name">Nom et prénom</label>
                        <input 
                          type="text" 
                          id="name" 
                          name="name" 
                          value={contactForm.name}
                          onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                          required 
                        />
                      </FormGroup>
                      
                      <FormGroup>
                        <label htmlFor="email">Email</label>
                        <input 
                          type="email" 
                          id="email" 
                          name="email" 
                          value={contactForm.email}
                          onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                          required 
                        />
                      </FormGroup>
                      
                      <FormGroup>
                        <label htmlFor="subject">Sujet</label>
                        <input 
                          type="text" 
                          id="subject" 
                          name="subject" 
                          value={contactForm.subject}
                          onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                          required 
                        />
                      </FormGroup>
                      
                      <FormGroup>
                        <label htmlFor="message">Message</label>
                        <textarea 
                          id="message" 
                          name="message" 
                          value={contactForm.message}
                          onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                          required
                        ></textarea>
                      </FormGroup>
                      
                      {submitStatus === 'success' && (
                        <div style={{ color: '#28a745', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <FaCheck />
                          {submitMessage}
                        </div>
                      )}
                      
                      {submitStatus === 'error' && (
                        <div style={{ color: '#dc3545', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <FaExclamationTriangle />
                          {submitMessage}
                        </div>
                      )}
                      
                      <SubmitButton type="submit" disabled={isSubmitting}>
                        {isSubmitting ? (
                          <>Envoi en cours...</>
                        ) : (
                          <>
                            <FaPaperPlane style={{ marginRight: '8px' }} />
                            Envoyer le message
                          </>
                        )}
                      </SubmitButton>
                    </ContactForm>
                  </TabContent>
                )}
                
                {activeSection === 'licence' && (
                  <TabContent
                    key="licence"
                    variants={contentVariants}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                  >
                    <h2>Licence d'utilisation</h2>
                    <p>Le site web www.offisport.com, y compris son code source, sa structure, son design et son contenu original, constitue une œuvre protégée par les lois françaises et internationales sur la propriété intellectuelle et le droit d'auteur.</p>
                    
                    <h3>Propriété intellectuelle</h3>
                    <p>Le site web www.offisport.com a été conçu et développé par <a href="https://glweb.fr/" target="_blank" rel="noopener noreferrer">GLWeb</a>. Conformément aux articles L.111-1 et suivants du Code de la propriété intellectuelle français, Guillaume LEFEBVRE (GLWeb) est et demeure le titulaire exclusif des droits de propriété intellectuelle sur :</p>
                    <ul>
                      <li>L'ensemble du code source du site</li>
                      <li>L'architecture technique et la structure du site</li>
                      <li>Les éléments graphiques et le design créés pour le site</li>
                      <li>Tout développement, amélioration ou modification apportés au site</li>
                    </ul>
                    
                    <h3>Licence d'utilisation accordée à l'OSARC</h3>
                    <p>Guillaume LEFEBVRE (GLWeb) accorde à l'Office des Sports de l'Agglomération de la Région de Compiègne (OSARC) une licence d'utilisation non exclusive et non transférable, permettant :</p>
                    <ul>
                      <li>L'utilisation du site web dans le cadre de ses activités</li>
                      <li>L'ajout, la modification et la suppression de contenu via l'interface d'administration Strapi mise à disposition</li>
                      <li>La gestion des utilisateurs et des droits d'accès à l'interface d'administration</li>
                    </ul>
                    
                    <h3>Restrictions</h3>
                    <p>Sans l'autorisation écrite expresse et préalable de Guillaume LEFEBVRE (GLWeb), il est strictement interdit à quiconque, y compris l'OSARC, de :</p>
                    <ul>
                      <li>Modifier, adapter ou transformer le code source du site</li>
                      <li>Réutiliser tout ou partie du code source pour d'autres projets</li>
                      <li>Copier, reproduire ou distribuer le code source à des tiers</li>
                      <li>Créer des œuvres dérivées basées sur le code source du site</li>
                      <li>Céder, vendre, sous-licencier ou transférer les droits sur le site à un tiers</li>
                      <li>Supprimer ou modifier les mentions de propriété intellectuelle présentes sur le site</li>
                    </ul>
                    
                    <h3>Maintenance et évolutions</h3>
                    <p>Toute modification technique, évolution fonctionnelle ou correction de bugs doit être réalisée exclusivement par GLWeb (ou Guillaume LEFEBVRE) ou avec son autorisation écrite explicite.</p>
                    
                    <h3>Protection juridique</h3>
                    <p>Cette licence est protégée par les dispositions du Code de la propriété intellectuelle français, la directive 2009/24/CE du Parlement européen et du Conseil du 23 avril 2009 concernant la protection juridique des programmes d'ordinateur, ainsi que par la Convention de Berne pour la protection des œuvres littéraires et artistiques.</p>
                    
                    <h3>Sanctions</h3>
                    <p>Toute violation de ces dispositions constitue une contrefaçon sanctionnée par les articles L.335-2 et suivants du Code de la propriété intellectuelle, pouvant entraîner des sanctions civiles et pénales, incluant jusqu'à trois ans d'emprisonnement et 300 000 euros d'amende, ainsi que d'éventuels dommages et intérêts.</p>
                  </TabContent>
                )}
              </AnimatePresence>
            </ContentArea>
          </PopupContent>
        </PopupContainer>
      </PopupOverlay>
    </AnimatePresence>
  );
};

export default LegalPopup; 